import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card, Pill, Empty, Btn, Modal } from "@/dashboard/ui";
import SubscriptionPlansAdmin from "@/dashboard/SubscriptionPlansAdmin";
import { ShieldCheck, Building2, ScrollText, Bell, Plug, Tag, Headphones, PhoneCall } from "lucide-react";

const TABS = [
  { id: "overview", label: "Overview", icon: ShieldCheck },
  { id: "plans", label: "Plans & Pricing", icon: Tag },
  { id: "businesses", label: "Businesses", icon: Building2 },
  { id: "integrations", label: "Integrations", icon: Plug },
  { id: "telephony", label: "Telephony", icon: PhoneCall },
  { id: "support", label: "Support Tickets", icon: Headphones },
  { id: "audit", label: "Security Log", icon: ScrollText },
  { id: "notifications", label: "Notifications", icon: Bell },
];

function TelephonyTab() {
  const [data, setData] = useState(null);
  const [limits, setLimits] = useState({});
  const load = useCallback(() => {
    api.get("/admin/telephony/overview").then((r) => setData(r.data)).catch(() => setData({ tenants: [], totals: {} }));
  }, []);
  useEffect(load, [load]);
  const saveLimit = async (uid) => {
    try {
      await api.put("/admin/telephony/limits", { user_id: uid, monthly_minutes: Number(limits[uid]) });
      load();
    } catch { /* surfaced via reload state */ }
  };
  if (!data) return <p className="text-sm text-neutral-500">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="owner-telephony-tab">
      <div className="flex flex-wrap gap-3">
        {[["Inbound answering", data.inbound_enabled ? "ENABLED" : "Disabled (flag off)"],
          ["Provider credentials", data.provider_configured ? "Configured" : "Not configured"],
          ["Number provisioning", data.provisioning_live ? "LIVE" : "Simulated (mock)"],
          ["Call transfers", data.call_control_live ? "LIVE" : "Simulated (mock)"]].map(([l, v]) => (
          <Card key={l} className="px-4 py-3"><p className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">{l}</p><p className="text-sm font-semibold">{v}</p></Card>
        ))}
      </div>
      <Card className="p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Tenant usage — {data.month}</h3>
          <p className="text-xs text-neutral-500">
            {data.totals?.active_numbers ?? 0} active numbers · {data.totals?.minutes_used ?? 0} min used ·{" "}
            {data.totals?.flagged ?? 0} flagged · default cap {data.monthly_cap_default} min/mo · max {data.max_concurrent_calls} concurrent
          </p>
        </div>
        {(data.tenants || []).length === 0 ? (
          <p className="text-xs text-neutral-500">No telephony activity yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs" data-testid="telephony-tenants-table">
              <thead><tr className="border-b border-neutral-100 text-neutral-400">
                <th className="py-2 pr-3">Tenant</th><th className="pr-3">Numbers</th><th className="pr-3">Minutes used</th>
                <th className="pr-3">Monthly cap</th><th className="pr-3">In progress</th><th className="pr-3">Flag</th><th>Set cap</th>
              </tr></thead>
              <tbody>
                {data.tenants.map((t) => (
                  <tr key={t.user_id} className="border-b border-neutral-50" data-testid={`tel-row-${t.user_id}`}>
                    <td className="py-2 pr-3">{t.email || t.user_id.slice(0, 8)}</td>
                    <td className="pr-3">{t.active_numbers}/{t.numbers}</td>
                    <td className="pr-3">{t.minutes_used}</td>
                    <td className="pr-3">{t.monthly_cap}</td>
                    <td className="pr-3">{t.in_progress}</td>
                    <td className="pr-3">{t.flagged ? <Pill tone="amber">review</Pill> : <Pill tone="green">ok</Pill>}</td>
                    <td className="flex items-center gap-1 py-1">
                      <input type="number" min="1" className="w-20 rounded-lg border border-neutral-200 px-2 py-1 text-xs"
                        data-testid={`tel-cap-${t.user_id}`} placeholder={String(t.monthly_cap)}
                        value={limits[t.user_id] ?? ""} onChange={(e) => setLimits({ ...limits, [t.user_id]: e.target.value })} />
                      <button className="rounded-lg bg-[#111111] px-2 py-1 text-[11px] font-semibold text-white"
                        data-testid={`tel-cap-save-${t.user_id}`} onClick={() => saveLimit(t.user_id)}>Save</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function CapabilityRow({ label, ok, note }) {
  return (
    <div className="flex items-center justify-between border-b border-neutral-100 py-2 text-sm last:border-0">
      <span className="text-neutral-600">{label}</span>
      {ok ? <Pill tone="black">Yes</Pill> : <Pill tone="outline">{note || "No"}</Pill>}
    </div>
  );
}

function ProviderCard({ name, data, testid, children }) {
  const connected = (data?.connected_accounts ?? 0) > 0;
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-[#111111]">{name}</p>
        {connected ? <Pill tone="black">Connected</Pill> : <Pill tone="outline">Not connected</Pill>}
      </div>
      <div className="mt-3">
        <CapabilityRow label="Credentials configured" ok={data?.credentials_configured} note={data?.credentials_configured ? "Yes" : "Setup required"} />
        <CapabilityRow label={`Verified connections (${data?.connected_accounts ?? 0})`} ok={connected} note="None" />
      </div>
      {(data?.accounts || []).map((a, i) => (
        <div key={i} className="mt-2 rounded-xl border border-neutral-200 bg-neutral-50/60 p-3 text-xs">
          <p className="font-semibold">{a.external_name || "Connected account"}</p>
          <p className="mt-0.5 text-neutral-500">Permissions: {(a.permissions || []).join(", ") || "none recorded"}</p>
          {a.last_verified_at && <p className="text-neutral-400">Last verified: {String(a.last_verified_at).slice(0, 10)}</p>}
        </div>
      ))}
      {data?.note && <p className="mt-2 text-[11px] text-neutral-500">{data.note}</p>}
      {children}
    </Card>
  );
}

function IntegrationsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/integrations").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);
  if (!data) return <p className="text-sm text-neutral-400">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="owner-integrations">
      <p className="text-xs text-neutral-500">
        "Credentials configured" means keys exist in configuration — it never counts as a verified connection.
        Connecting a provider uses that provider's OAuth flow; no connection is claimed until it is verified.
      </p>
      <div className="grid gap-4 lg:grid-cols-2">
        <ProviderCard name="Facebook" data={data.facebook} testid="integration-facebook">
          <div className="mt-3 space-y-1">
            <CapabilityRow label="Publishing capability" ok={false} note="Per connected Page" />
            <CapabilityRow label="Lead Ads webhooks" ok={false} note="Separate setup" />
          </div>
          {data.facebook?.capabilities && (
            <ul className="mt-2 space-y-1 text-[11px] text-neutral-500">
              {Object.values(data.facebook.capabilities).map((c, i) => <li key={i}>· {c}</li>)}
            </ul>
          )}
        </ProviderCard>
        <ProviderCard name="Instagram" data={data.instagram} testid="integration-instagram" />
        <ProviderCard name="LinkedIn" data={data.linkedin} testid="integration-linkedin" />
        <Card className="p-5" data-testid="integration-stripe">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-[#111111]">Stripe billing</p>
            <Pill tone={data.stripe?.live_charges ? "red" : "outline"}>
              {data.stripe?.live_charges ? "LIVE" : `Test mode${data.stripe?.configured ? "" : " — not configured"}`}
            </Pill>
          </div>
          <p className="mt-2 text-[11px] text-neutral-500">No real charges occur in test mode. Live mode activates only after owner approval and live-key configuration.</p>
        </Card>
        <Card className="p-5" data-testid="integration-email">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-[#111111]">Email delivery</p>
            {data.email?.configured ? <Pill tone="black">Configured</Pill> : <Pill tone="outline">Not configured</Pill>}
          </div>
          <p className="mt-2 text-[11px] text-neutral-500">Delivery is not verified — reset/notification emails are sent best-effort and never claimed as delivered.</p>
        </Card>
        <Card className="p-5" data-testid="integration-telephony">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-[#111111]">Telephony</p>
            <Pill tone="outline">Provider not connected</Pill>
          </div>
          <div className="mt-3">
            <CapabilityRow label="Inbound flag enabled" ok={data.telephony_inbound?.flag_enabled} note="Off" />
            <CapabilityRow label="Outbound platform switch" ok={data.telephony_outbound?.platform_enabled} note="Off" />
            <CapabilityRow label="Provider connected" ok={data.telephony_inbound?.provider_connected} note="No" />
          </div>
        </Card>
      </div>
    </div>
  );
}

function StatusPill({ ok, note }) {
  return ok
    ? <Pill tone="black">Configured</Pill>
    : <Pill tone="outline">{note || "Pending setup"}</Pill>;
}

function IntegrationRow({ name, detail, ok, note }) {
  return (
    <div className="flex items-center justify-between border-b border-neutral-100 py-3 last:border-0" data-testid={`integration-${name.toLowerCase().replace(/\s+/g, "-")}`}>
      <div>
        <p className="text-sm font-semibold text-[#111111]">{name}</p>
        <p className="text-xs text-neutral-500">{detail}</p>
      </div>
      <StatusPill ok={ok} note={note} />
    </div>
  );
}

function SupportTicketsTab() {
  return (
    <div data-testid="owner-support-tickets" className="space-y-4">
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-[#111111]">Support Inbox — support@savykernoai.com</p>
            <p className="mt-1 text-xs text-neutral-500">
              Google Workspace mailbox integration for incoming support email.
            </p>
          </div>
          <Pill tone="outline">Connection required</Pill>
        </div>
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800" data-testid="support-connection-required">
          The Google Workspace support mailbox is not connected yet. Connecting it uses Google's authorised
          OAuth flow (no password is ever collected). Once connected, new support emails will appear here as
          tickets and replies will send from support@savykernoai.com.
        </div>
        <div className="mt-4 space-y-2 text-xs text-neutral-500">
          <p className="font-semibold text-neutral-600">To connect, the following is needed:</p>
          <ul className="list-inside list-disc space-y-1">
            <li>A Google Cloud project with the Gmail API enabled</li>
            <li>OAuth credentials (Client ID + secret) for the support mailbox</li>
            <li>Authorisation from the support@savykernoai.com account</li>
          </ul>
          <p className="pt-1 text-neutral-400">Until then, no emails are synced and none are sent — this panel is intentionally inert.</p>
        </div>
      </Card>
    </div>
  );
}

function OverviewTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch(() => setErr("Could not load owner overview."));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (err) return <p className="text-sm text-red-600" data-testid="owner-overview-error">{err}</p>;
  if (!data) return <p className="text-sm text-neutral-400">Loading…</p>;
  const { totals, plans, integrations } = data;
  const cards = [
    ["Businesses", totals.users],
    ["Active subscriptions", totals.active_subscriptions],
    ["Unpaid / suspended", totals.unpaid_or_suspended],
    ["New this week", totals.new_registrations_7d],
    ["Leads (all tenants)", totals.leads],
    ["Appointments", totals.appointments],
    ["Voice agents", totals.voice_agents],
  ];
  return (
    <div className="space-y-6" data-testid="owner-overview">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {cards.map(([label, val]) => (
          <Card key={label} className="p-4">
            <p className="text-2xl font-bold text-[#111111]" data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{val}</p>
            <p className="mt-1 text-xs text-neutral-500">{label}</p>
          </Card>
        ))}
      </div>
      <Card className="p-5">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-bold text-[#111111]"><Plug size={15} /> Integration status</h3>
        <IntegrationRow name="Billing (Stripe)" detail={integrations.stripe.configured ? `Mode: ${integrations.stripe.mode}` : "Not configured"} ok={integrations.stripe.configured} />
        <IntegrationRow name="Email notifications" detail="Owner alerts by email" ok={integrations.email.configured} note="Not configured" />
        <IntegrationRow name="Meta social" detail="Facebook / Instagram publishing" ok={integrations.meta_social.configured} />
        <IntegrationRow name="Inbound telephony" detail={integrations.telephony.inbound_enabled ? "Flag on — awaiting provider verification" : "Disabled"} ok={integrations.telephony.configured} note={integrations.telephony.inbound_enabled ? "Pending verification" : "Coming soon"} />
        <IntegrationRow name="Outbound calling" detail="Control plane only — no live calls" ok={integrations.calling.configured} note="Provider not connected" />
      </Card>
      <Card className="p-5">
        <h3 className="mb-3 text-sm font-bold text-[#111111]">Plan pricing</h3>
        <div className="space-y-2">
          {plans.map((p) => (
            <div key={p.id} className="flex items-center justify-between text-sm" data-testid={`owner-plan-${p.id}`}>
              <span className="font-medium">{p.name} <span className="text-xs text-neutral-400">v{p.price_version}</span></span>
              <span className="font-semibold">£{(p.price_pence / 100).toFixed(0)}/mo</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function BusinessesTab() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = { page, per_page: 25 };
      if (search) params.search = search;
      if (plan) params.plan = plan;
      if (status) params.status = status;
      api.get("/admin/businesses", { params }).then((r) => setData(r.data)).catch(() => setData({ businesses: [], total: 0 }));
    }, 250);
    return () => clearTimeout(t);
  }, [search, plan, status, page]);

  const rows = data?.businesses;
  return (
    <div data-testid="owner-businesses">
      <div className="mb-4 flex flex-wrap gap-2">
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search name, email or phone…"
          data-testid="owner-biz-search"
          className="w-full max-w-xs rounded-xl border border-neutral-300 px-3.5 py-2 text-sm outline-none focus:border-[#111111]"
        />
        <select value={plan} onChange={(e) => { setPlan(e.target.value); setPage(1); }} data-testid="owner-biz-plan-filter"
          className="rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none">
          <option value="">All plans</option>
          <option value="starter">Starter</option>
          <option value="growth">Growth</option>
          <option value="pro">Pro</option>
        </select>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} data-testid="owner-biz-status-filter"
          className="rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none">
          <option value="">All statuses</option>
          <option value="active">active</option>
          <option value="unpaid">unpaid</option>
          <option value="suspended">suspended</option>
        </select>
      </div>
      {!rows ? (
        <p className="text-sm text-neutral-400">Loading…</p>
      ) : rows.length === 0 ? (
        <Empty title="No businesses match." testid="owner-businesses-empty" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-xs uppercase tracking-wide text-neutral-400">
                <th className="py-2 pr-4 font-semibold">Business</th>
                <th className="py-2 pr-4 font-semibold">Email</th>
                <th className="py-2 pr-4 font-semibold">Phone</th>
                <th className="py-2 pr-4 font-semibold">Plan</th>
                <th className="py-2 pr-4 font-semibold">Status</th>
                <th className="py-2 pr-4 font-semibold">Joined</th>
                <th className="py-2 font-semibold">Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} className="border-b border-neutral-100" data-testid={`owner-business-${b.id}`}>
                  <td className="py-2.5 pr-4 font-medium">{b.business_name || b.name}</td>
                  <td className="py-2.5 pr-4 text-neutral-600">{b.email}</td>
                  <td className="py-2.5 pr-4 text-neutral-600">{b.business_phone || <span className="text-neutral-300">—</span>}</td>
                  <td className="py-2.5 pr-4">{b.plan_id || <span className="text-neutral-300">—</span>}</td>
                  <td className="py-2.5 pr-4">
                    {b.access_status === "active" ? <Pill tone="black">active</Pill> : <Pill tone="outline">{b.access_status || "none"}</Pill>}
                  </td>
                  <td className="py-2.5 pr-4 text-neutral-500">{String(b.created_at || "").slice(0, 10)}</td>
                  <td className="py-2.5">
                    <Btn size="sm" variant="ghost" data-testid={`biz-details-${b.id}`} onClick={() => setDetailId(b.id)}>View</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.pages > 1 && (
        <div className="mt-4 flex items-center gap-2 text-xs text-neutral-500" data-testid="owner-biz-pagination">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            data-testid="owner-biz-prev"
            className="rounded-full border border-neutral-300 px-3 py-1.5 font-semibold disabled:opacity-40"
          >
            ← Prev
          </button>
          <span>Page {data.page} of {data.pages} · {data.total} businesses</span>
          <button
            onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
            disabled={page >= data.pages}
            data-testid="owner-biz-next"
            className="rounded-full border border-neutral-300 px-3 py-1.5 font-semibold disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      )}
      {detailId && <BusinessDetailModal bizId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function BusinessDetailModal({ bizId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/admin/businesses/${bizId}`).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [bizId]);
  const Row = ({ label, value }) => (
    <div className="flex items-start justify-between gap-4 border-b border-neutral-50 py-1.5 text-xs">
      <span className="text-neutral-400">{label}</span>
      <span className="text-right font-medium text-[#111111]">{value ?? "—"}</span>
    </div>
  );
  return (
    <Modal title={d && !d.error ? (d.business_name || d.name || "Business details") : "Business details"}
      onClose={onClose} testid="biz-detail-modal">
      {!d ? <p className="text-sm text-neutral-400">Loading…</p> : d.error ? (
        <p className="text-sm text-red-500">Could not load this business.</p>
      ) : (
        <div className="space-y-4">
          <div>
            <h4 className="mb-1 text-[10px] font-bold uppercase tracking-widest text-neutral-400">Profile</h4>
            <Row label="Email" value={d.email} />
            <Row label="Industry" value={d.industry} />
            <Row label="Phone" value={d.business_phone} />
            <Row label="Joined" value={String(d.created_at || "").slice(0, 10)} />
            <Row label="Last sign-in" value={d.last_login_at ? String(d.last_login_at).slice(0, 16).replace("T", " ") : null} />
          </div>
          <div>
            <h4 className="mb-1 text-[10px] font-bold uppercase tracking-widest text-neutral-400">Plan & account</h4>
            <Row label="Plan" value={d.plan?.name || d.plan?.id || "none"} />
            <Row label="Price" value={d.plan?.price_gbp != null ? `£${d.plan.price_gbp}/mo (locked v${d.plan.price_version || 1})` : null} />
            <Row label="Account status" value={d.access_status} />
            <Row label="Subscription" value={d.subscription?.status} />
            <Row label="Telephony minutes cap" value={d.telephony_monthly_minutes || "default"} />
            <Row label="Feature access" value={(d.plan?.features || []).join(", ") || null} />
          </div>
          <div>
            <h4 className="mb-1 text-[10px] font-bold uppercase tracking-widest text-neutral-400">Usage ({d.month})</h4>
            <Row label="Records" value={`${d.counts.leads} leads · ${d.counts.appointments} appointments · ${d.counts.agents} agents · ${d.counts.phone_numbers} numbers · ${d.counts.incoming_calls} calls`} />
            {Object.entries(d.usage_this_month || {}).map(([k, v]) => <Row key={k} label={k} value={v} />)}
          </div>
          <div>
            <h4 className="mb-1 text-[10px] font-bold uppercase tracking-widest text-neutral-400">Recent activity</h4>
            {(d.recent_activity || []).length === 0 ? (
              <p className="text-xs text-neutral-400">No recorded activity.</p>
            ) : d.recent_activity.map((a) => (
              <div key={a.id} className="flex items-center gap-2 border-b border-neutral-50 py-1 text-xs">
                <Pill tone={a.result === "success" ? "black" : a.result === "partial" ? "amber" : "red"}>{a.result}</Pill>
                <span className="font-medium">{a.action}</span>
                <span className="ml-auto text-neutral-400">{String(a.at).slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

function AuditTab() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [result, setResult] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback((p) => {
    const params = { page: p, limit: 25 };
    if (q.trim()) params.q = q.trim();
    if (action) params.action = action;
    if (result) params.result = result;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    api.get("/admin/audit-logs", { params }).then((r) => setData(r.data)).catch(() => setData({ logs: [], total: 0, pages: 1, actions: [] }));
  }, [q, action, result, fromDate, toDate]);

  useEffect(() => { load(page); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const search = () => { setPage(1); load(1); };

  if (!data) return <p className="text-sm text-neutral-400">Loading…</p>;
  const inputSm = "rounded-lg border border-neutral-200 px-2.5 py-1.5 text-xs outline-none focus:border-[#111111]";
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2" data-testid="audit-filters">
        <input className={inputSm + " w-52"} placeholder="Search detail, action, actor…" value={q}
          data-testid="audit-search" onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()} />
        <select className={inputSm} value={action} data-testid="audit-action-filter" onChange={(e) => setAction(e.target.value)}>
          <option value="">All activity types</option>
          {(data.actions || []).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className={inputSm} value={result} data-testid="audit-result-filter" onChange={(e) => setResult(e.target.value)}>
          <option value="">All results</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="partial">Partial</option>
        </select>
        <input type="date" className={inputSm} value={fromDate} data-testid="audit-from" onChange={(e) => setFromDate(e.target.value)} />
        <input type="date" className={inputSm} value={toDate} data-testid="audit-to" onChange={(e) => setToDate(e.target.value)} />
        <Btn size="sm" data-testid="audit-apply" onClick={search}>Apply</Btn>
      </div>
      <p className="text-xs text-neutral-500">{data.total} event{data.total === 1 ? "" : "s"} · page {data.page} of {data.pages}</p>
      {data.logs.length === 0 ? (
        <Empty title="No events match these filters." testid="owner-audit-empty" />
      ) : (
        <div className="space-y-2" data-testid="owner-audit-log">
          {data.logs.map((l) => (
            <div key={l.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-neutral-100 px-4 py-2.5 text-sm">
              {l.result === "success" ? <Pill tone="black">success</Pill> : l.result === "partial" ? <Pill tone="amber">partial</Pill> : <Pill tone="red">{l.result}</Pill>}
              <span className="font-semibold text-[#111111]">{l.action}</span>
              {l.detail && <span className="text-neutral-500">{l.detail}</span>}
              <span className="ml-auto text-xs text-neutral-400">{String(l.at).replace("T", " ").slice(0, 16)}</span>
            </div>
          ))}
        </div>
      )}
      {data.pages > 1 && (
        <div className="flex items-center gap-2" data-testid="audit-pagination">
          <Btn size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>← Prev</Btn>
          <span className="text-xs text-neutral-500">{page} / {data.pages}</span>
          <Btn size="sm" variant="ghost" disabled={page >= data.pages} onClick={() => setPage(page + 1)}>Next →</Btn>
        </div>
      )}
    </div>
  );
}

function NotificationsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/notifications?limit=200").then((r) => setData(r.data)).catch(() => setData({ notifications: [], email_configured: false }));
  }, []);
  if (!data) return <p className="text-sm text-neutral-400">Loading…</p>;
  return (
    <div className="space-y-3" data-testid="owner-notifications">
      {!data.email_configured && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="owner-email-not-configured">
          Email notifications are not configured — events below are recorded here but no emails are being sent.
        </div>
      )}
      {data.notifications.length === 0 && <Empty title="No events yet." body="New registrations and billing events appear here." testid="owner-notifications-empty" />}
      {data.notifications.map((n) => (
        <div key={n.id} className="rounded-xl border border-neutral-100 px-4 py-3 text-sm">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[#111111]">{n.subject}</span>
            {n.email_sent ? <Pill tone="black">Emailed</Pill> : <Pill tone="grey">Console only</Pill>}
            <span className="ml-auto text-xs text-neutral-400">{String(n.at).replace("T", " ").slice(0, 16)}</span>
          </div>
          {n.detail && <p className="mt-1 text-neutral-500">{n.detail}</p>}
        </div>
      ))}
    </div>
  );
}

export default function OwnerConsole() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  if (user && user.role !== "admin") {
    return <Empty title="Platform owner access only." testid="owner-console-denied" />;
  }
  return (
    <div className="space-y-5" data-testid="owner-console">
      <div>
        <h1 className="text-xl font-bold tracking-tight text-[#111111]">Owner Console</h1>
        <p className="text-sm text-neutral-500">Platform-wide visibility. Integration states are honest — nothing here is simulated.</p>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="owner-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`owner-tab-${t.id}`}
              className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${tab === t.id ? "bg-[#111111] text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`}
            >
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "overview" && <OverviewTab />}
      {tab === "plans" && (
        <div data-testid="owner-plans-tab">
          <p className="mb-3 text-xs text-neutral-500">
            Global plan pricing lives here — owner only. Publishing a new price creates a new price version; existing subscribers keep their agreed price.
          </p>
          <SubscriptionPlansAdmin />
        </div>
      )}
      {tab === "businesses" && <BusinessesTab />}
      {tab === "integrations" && <IntegrationsTab />}
      {tab === "telephony" && <TelephonyTab />}
      {tab === "support" && <SupportTicketsTab />}
      {tab === "audit" && <AuditTab />}
      {tab === "notifications" && <NotificationsTab />}
    </div>
  );
}
